import React, { ReactNode, createContext, useContext, useState, useEffect } from 'react';
import IColorInfo from '../interfaces/IColorInfo';
import * as d3 from 'd3';
import IChoroplethColors from '../interfaces/IChoroplethColors';
import COLORS, { NO_VALUE_COLOR } from '../constants/colors';

interface IVariables {
	choroplethData?: any;
	min?: number;
	max?: number;
	hoveredPolygon?: any;
	choroplethColorInfo?: IColorInfo[];
	choroplethColors?: any;
	choroplethOpacityValues?: any;
}

interface IFunctions {
	setHoveredPolygon?: any;
	setChoroplethColorInfo?: any;
	setChoroplethData?: any;
}

const ChoroplethContext = createContext<[IVariables, IFunctions]>([{}, {}]);

interface IProvider {
	children: ReactNode;
}
export const ChoroplethProvider = (props: IProvider) => {
	const [choroplethData, setChoroplethData] = useState<any>();
	const [hoveredPolygon, setHoveredPolygon] = useState<any>();
	const [choroplethColorInfo, setChoroplethColorInfo] = useState<IColorInfo[]>();
	const [min, setMin] = useState<number>(0);
	const [max, setMax] = useState<number>(0);
	const [choroplethColors, setChoroplethColors] = useState<IChoroplethColors>({});
	const [choroplethOpacityValues, setChoroplethOpacityValues] = useState<any>();

	useEffect(() => {
		const values: number[] = Object.values(choroplethData ?? {}) ?? [];
		const extent = d3.extent(values);
		const min = extent?.[0];
		const max = extent?.[1];
		setMin(min);
		setMax(max);
	}, [choroplethData]);

	useEffect(() => {
		const rtn: any = ['match', ['get', 'TagId'], hoveredPolygon?.properties?.TagId ?? -1, 0.7];
		Object.keys(choroplethData ?? {}).forEach((key: string) => {
			const value = choroplethData[key];
			if ((value !== 0 && Number(key) !== hoveredPolygon?.properties?.TagId) ?? -1) {
				rtn.push(Number(key));
				rtn.push(0.3);
			}
		});
		rtn.push(0.3);
		setChoroplethOpacityValues(rtn);
	}, [choroplethData, hoveredPolygon]);

	useEffect(() => {
		let difference = max - min;
		if (difference < 6 || isNaN(min) || isNaN(max)) difference = 6;
		const step = Math.ceil(difference / 6);
		let rtn: IColorInfo[] = [];
		for (let i = 0; i < 6; i++) {
			const value = step * i + min;
			const maxValue = value + step - 1;
			rtn.push({ maxValue: maxValue, minValue: value, color: COLORS[i] });
		}
		rtn = rtn.sort((a: IColorInfo, b: IColorInfo) => {
			if (a.maxValue < b.maxValue) return 1;
			else if (a.maxValue > b.maxValue) return -1;
			else return 0;
		});
		setChoroplethColorInfo(rtn);
	}, [min, max]);

	useEffect(() => {}, []);

	useEffect(() => {
		const rtn: any = ['match', ['get', 'TagId']];
		Object.keys(choroplethData ?? {}).forEach((tagId: string) => {
			const value = Number(choroplethData[tagId]);
			let index = 0;
			for (index = 0; index < choroplethColorInfo.length; index++) {
				if (value <= choroplethColorInfo[index].maxValue && value >= choroplethColorInfo[index].minValue) {
					rtn.push(Number(tagId));
					rtn.push(choroplethColorInfo[index].color);
					break;
				}
			}
		});
		rtn.push(NO_VALUE_COLOR);
		setChoroplethColors(rtn);
	}, [min, max, choroplethData, choroplethColorInfo]);

	const value: [IVariables, IFunctions] = [
		{
			choroplethData,
			min,
			max,
			hoveredPolygon,
			choroplethColorInfo,
			choroplethColors,
			choroplethOpacityValues,
		},
		{
			setHoveredPolygon,
			setChoroplethColorInfo,
			setChoroplethData,
		},
	];

	return <ChoroplethContext.Provider value={value} {...props} />;
};

export const useChoroplethContext = () => {
	const context = useContext(ChoroplethContext);
	if (!context) {
		throw new Error('useChorplethContext must be used within a ChoroplethProvider');
	}
	return context;
};
